import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { SiteLegalAgreementsEnum } from '../../../graphql/gatsby'
import useLegalAgreementsApi, {
  OnAgreementAcceptedProps,
} from '../../../hooks/useLegalAgreementsApi'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { legalAgreementIds } from '../../../utils/legalAgreements'
import { ClickWrapModalContent } from '../clickwrap/ClickWrapModalContent'
import { ClickWrapContext } from '../clickwrap/context'
import { ClickWrapAdditionalActionsComponent, ClickWrapContentComponent } from '../clickwrap/types'
import CoveredEntityContextProvider from './data-providers/CoveredEntityContextProvider'
import BAAContent from './documents/BAAContent'
import PLAgreementContent from './documents/PLAgreementContent'
import PLAgreementContentActions from './documents/PLAgreementContentActions'
import RxDFHendCAContent from './documents/RxDFHendCAContent'
import { RxDFHendCAContentActions } from './documents/RxDFHendCAContentActions'
import RxDFHendUSContent from './documents/RxDFHendUSContent'
import RxDFHendUSContentActions from './documents/RxDFHendUSContentActions'

interface ReviewableAgreement {
  id: string
  components: {
    AgreementComponent: ClickWrapContentComponent
    AgreementAdditionalActions: ClickWrapAdditionalActionsComponent | null
    UserDataProviderContextProvider?: React.FC<{ children: React.ReactNode }>
  }
}

const agreementsBySet: Record<SiteLegalAgreementsEnum, ReviewableAgreement[]> = {
  US: [
    {
      id: legalAgreementIds.rxDFHendUSJun2024,
      components: {
        AgreementComponent: RxDFHendUSContent,
        AgreementAdditionalActions: RxDFHendUSContentActions,
      },
    },
    {
      id: legalAgreementIds.baa,
      components: {
        AgreementComponent: BAAContent,
        AgreementAdditionalActions: null,
        UserDataProviderContextProvider: CoveredEntityContextProvider,
      },
    },
  ],
  PL: [
    {
      id: legalAgreementIds.plOct052023,
      components: {
        AgreementComponent: PLAgreementContent,
        AgreementAdditionalActions: PLAgreementContentActions,
      },
    },
  ],
  CA: [
    {
      id: legalAgreementIds.rxDFHendCASept2021,
      components: {
        AgreementComponent: RxDFHendCAContent,
        AgreementAdditionalActions: RxDFHendCAContentActions,
      },
    },
  ],
}

export interface ReviewPendingAgreementProps {
  agreementId: string
}

const ReviewPendingAgreement: React.FC<ReviewPendingAgreementProps> = ({ agreementId }) => {
  const { legalAgreements } = useSiteMetadata() || {}
  const agreements = useMemo(
    () =>
      legalAgreements && legalAgreements in agreementsBySet ? agreementsBySet[legalAgreements] : [],
    [legalAgreements],
  )
  const pendingAgreement = useMemo(() => {
    const matchingAgreement = agreements.find(({ id }) => id === agreementId)
    if (!matchingAgreement) {
      console.error(`Agreement (id: ${agreementId}) not found for review`)
      return undefined
    }
    return matchingAgreement
  }, [agreementId, agreements])

  const [, clickWrapDispatch] = useContext(ClickWrapContext)

  const { loading: apiLoading, error: apiError, onAgreementAccepted } = useLegalAgreementsApi()

  const [userInput, setUserInput] = useState<OnAgreementAcceptedProps['userInput']>(undefined)

  useEffect(() => {
    if (!apiLoading) {
      clickWrapDispatch({ type: 'setAcceptPending', value: false })
    }
  }, [apiLoading, clickWrapDispatch])

  // update clickwrap context when agreementId changes
  useEffect(() => {
    clickWrapDispatch({ type: 'contentChanged' })
  }, [agreementId, clickWrapDispatch])

  const onAccept = useCallback(() => {
    if (pendingAgreement) {
      onAgreementAccepted({
        agreementId: pendingAgreement.id,
        ...(userInput && { userInput }),
      })
    }
  }, [userInput, onAgreementAccepted, pendingAgreement])

  const UserDataProviderContextProvider =
    pendingAgreement?.components?.UserDataProviderContextProvider || React.Fragment

  return pendingAgreement ? (
    <UserDataProviderContextProvider>
      <ClickWrapModalContent
        acceptError={apiError}
        onAccept={onAccept}
        setUserInput={setUserInput}
        Content={pendingAgreement.components.AgreementComponent}
        AdditionalActions={pendingAgreement.components.AgreementAdditionalActions}
      />
    </UserDataProviderContextProvider>
  ) : null
}

export default ReviewPendingAgreement
